import {
  hsbToOklch,
  hslToOklch,
  oklchColor,
  oklchToHsb,
  oklchToHsl,
  oklchToRgb,
  rgbToOklch,
  type OklchColor,
  type RgbColor,
} from "@/document/model/color";
import type { PickerMode } from "./types";

export interface ColorFieldState {
  x: number;
  y: number;
  gradientKey: number;
}

export function colorFieldState(mode: PickerMode, color: OklchColor): ColorFieldState {
  const rgb = oklchToRgb(color);
  const hsl = oklchToHsl(color);
  const hsb = oklchToHsb(color);

  switch (mode) {
    case "rgb":
      return {
        x: rgb.r / 255,
        y: 1 - rgb.g / 255,
        gradientKey: rgb.b,
      };
    case "hsl":
      return {
        x: hsl.s,
        y: 1 - hsl.l,
        gradientKey: hsl.h,
      };
    case "oklch":
      return {
        x: color.c / maxOklchChroma(),
        y: 1 - color.l,
        gradientKey: color.h,
      };
    case "hex":
    case "hsb":
      return {
        x: hsb.s,
        y: 1 - hsb.b,
        gradientKey: hsb.h,
      };
  }
}

export function colorFromField(mode: PickerMode, color: OklchColor, x: number, y: number): OklchColor {
  const normalizedX = clamp01(x);
  const normalizedY = clamp01(y);
  const rgb = oklchToRgb(color);
  const hsl = oklchToHsl(color);
  const hsb = oklchToHsb(color);

  switch (mode) {
    case "rgb":
      return rgbToOklch({
        ...rgb,
        r: Math.round(normalizedX * 255),
        g: Math.round((1 - normalizedY) * 255),
      });
    case "hsl":
      return hslToOklch({
        ...hsl,
        s: normalizedX,
        l: 1 - normalizedY,
      });
    case "oklch":
      return oklchColor(1 - normalizedY, normalizedX * maxOklchChroma(), color.h, color.alpha);
    case "hex":
    case "hsb":
      return hsbToOklch({
        ...hsb,
        s: normalizedX,
        b: 1 - normalizedY,
      });
  }
}

export function colorAtFieldPoint(mode: PickerMode, baseColor: OklchColor, x: number, y: number): RgbColor {
  return oklchToRgb(colorFromField(mode, baseColor, x, y));
}

export function maxOklchChroma(): number {
  return 0.4;
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}
